<script lang="ts" setup>
  import type { ProductVariantPickerResource } from '@storefront/resources';
  import { computed } from 'vue';
  import { useTranslator, useRouter } from '@othercommerce/foundation';

  interface Props {
    variant: ProductVariantPickerResource,
    requestDefaultData?: {
      firstName: string,
      lastName: string,
      phone?: string,
      email: string,
      company?: string,
      vatId?: string
    }
  }

  defineProps<Props>();

  const router = useRouter();
  const translator = useTranslator();


  const privacyPolicy = computed(() => {
    return translator.trans('SHOP.PRODUCT.MESSAGE.REQUEST_PRODUCT_PRIVACY_NOTICE', { url: router.route('web.privacyPolicy.index') });
  });
</script>
<template>
  <UiModal aria-labelledby="product-request-modal-title" class="request" medium open visitable>
    <section class="request-container">
      <div class="request-col centered">
        <MediaPicture :photo="variant.photo" sizes="300x300 2x min 577, 150x150 min 577, 150x150 2x, 75x75" />
      </div>
      <div class="request-col">
        <SfHeading id="product-request-modal-title" type="h2">{{ $t('SHOP.PRODUCT.HEADER.REQUEST_PRODUCT', { name: variant.title || '' }) }}</SfHeading>
        <p class="request__product-description">{{ $t('SHOP.PRODUCT.MESSAGE.REQUEST_PRODUCT') }}</p>
      </div>
    </section>
    <FormController :action="$route('web.product.request.store')" :data="{ variant: variant.id, product: variant.productId, ...requestDefaultData }" aria-labelledby="product-request-modal-title" method="POST">
      <div class="request__inputs">
        <SfFormInput :label="$t('SHOP.PRODUCT.LABEL.FIRSTNAME')" name="firstName" required />
        <SfFormInput :label="$t('SHOP.PRODUCT.LABEL.LASTNAME')" name="lastName" required />
        <SfFormInput :label="$t('SHOP.PRODUCT.LABEL.COMPANY')" name="company" />
        <SfFormInput :label="$t('SHOP.PRODUCT.LABEL.VAT_ID')" :required="variant.isMedicinalProduct" name="vatId" />
        <SfFormInput :label="$t('SHOP.PRODUCT.LABEL.EMAIL')" name="email" required type="email" />
        <SfFormInput :label="$t('SHOP.PRODUCT.LABEL.PHONE')" name="phone" type="tel" />
        <SfFormNumber :label="$t('SHOP.PRODUCT.LABEL.REQUESTED_QUANTITY')" min="1" name="requestedNumber" required />
        <SfFormTextarea :label="$t('SHOP.PRODUCT.LABEL.ADDITIONAL_INFO')" name="note" />
        <SfFormSwitch name="acceptRodo" required>
          <span class="request__privacy-policy" v-html="privacyPolicy" />
        </SfFormSwitch>
      </div>
      <div class="actions">
        <SfButton primary type="submit">
          {{ $t('SHOP.PRODUCT.ACTION.SEND_PRODUCT_REQUEST') }}
        </SfButton>
        <SfButton back preserveScroll>
          {{ $t('SHOP.ACTION.CANCEL') }}
        </SfButton>
      </div>
    </FormController>
  </UiModal>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .request {
    &__inputs {
      display: flex;
      flex-direction: column;
      gap: var(--form-gap-y) var(--form-gap-x);
    }

    &__product-description {
      font-size: 0.875rem;
    }

    &__privacy-policy a {
      text-decoration: underline;
    }
  }

  .request-container {
    display: grid;
    grid-template-columns: 150px 1fr;
    align-items: start;
    gap: 1rem;

    .request-col {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0.5rem;

      h2 {
        font-size: 1.125rem;
      }
    }
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 2rem;

    .col {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .stock {
      min-width: 0;
    }
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  @include media.query(sm) {
    .request-container {
      grid-template-columns: 75px 1fr;

      .request-col h2 {
        font-size: 1rem;
      }
    }

    .row {
      flex-direction: column;
      gap: 1rem;
    }
  }
</style>
